if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.quantityForm = this.querySelector('.product-form__quantity');
        this.currentVariantInput = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('[type="submit"]');

        this.onOptionChange = this.onOptionChange.bind(this);
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        this.variantSelects = this.querySelector('variant-selects');
        this.variantRadios = this.querySelector('variant-radios');

        this.addVariantListeners();

        if (this.quantityInput && this.quantityForm) {
          this.setQuantityBoundries();

          if (!this.dataset.originalSection) {
            this.cartUpdateUnsubscriber = subscribe(
              PUB_SUB_EVENTS.cartUpdate,
              this.fetchQuantityRules.bind(this)
            );
          }

          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
            if (event.data.sectionId !== sectionId) return;

            this.updateQuantityRules(sectionId, event.data.html);
            this.setQuantityBoundries();
          });
        }
      }

      disconnectedCallback() {
        this.removeVariantListeners();

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      addVariantListeners() {
        if (this.variantSelects) {
          this.variantSelects.querySelectorAll('select').forEach((select) => {
            select.addEventListener('change', this.onOptionChange);
          });

          this.variantSelects.querySelectorAll('input[type="radio"]').forEach((radio) => {
            radio.addEventListener('change', this.onOptionChange);
          });
        }

        if (this.variantRadios) {
          this.variantRadios.querySelectorAll('input[type="radio"]').forEach((radio) => {
            radio.addEventListener('change', this.onOptionChange);
          });
        }
      }

      removeVariantListeners() {
        if (this.variantSelects) {
          this.variantSelects.querySelectorAll('select').forEach((select) => {
            select.removeEventListener('change', this.onOptionChange);
          });

          this.variantSelects.querySelectorAll('input[type="radio"]').forEach((radio) => {
            radio.removeEventListener('change', this.onOptionChange);
          });
        }

        if (this.variantRadios) {
          this.variantRadios.querySelectorAll('input[type="radio"]').forEach((radio) => {
            radio.removeEventListener('change', this.onOptionChange);
          });
        }
      }

      onOptionChange() {
        const variant = this.getSelectedVariant();
        if (!variant) return;

        this.updateVariantId(variant.id);
        this.renderVariantInfo(variant.id);
      }

      getSelectedOptions() {
        const selectEls = this.querySelectorAll('variant-selects select');
        if (selectEls.length) {
          return Array.from(selectEls).map((select) => select.value);
        }

        const fieldsets = this.querySelectorAll('variant-selects fieldset, variant-radios fieldset');
        if (fieldsets.length) {
          return Array.from(fieldsets).map((fieldset) => {
            const checked = fieldset.querySelector('input:checked');
            return checked ? checked.value : null;
          });
        }

        return [];
      }

      getVariantData() {
        const jsonEl = this.querySelector('variant-selects script[type="application/json"], variant-radios script[type="application/json"]');
        if (!jsonEl) return [];

        try {
          return JSON.parse(jsonEl.textContent);
        } catch (error) {
          console.error('Failed to parse variant JSON:', error);
          return [];
        }
      }

      getSelectedVariant() {
        const selectedOptions = this.getSelectedOptions();
        const variants = this.getVariantData();

        return variants.find((variant) => {
          return variant.options.every((option, index) => option === selectedOptions[index]);
        });
      }

      updateVariantId(variantId) {
        this.querySelectorAll('input.product-variant-id, form[action*="/cart/add"] input[name="id"]').forEach((input) => {
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        if (this.currentVariantInput) {
          this.currentVariantInput.value = variantId;
        }
      }

      renderVariantInfo(variantId) {
        const sectionId = this.dataset.section;
        const url = `${this.dataset.url}?variant=${variantId}&section_id=${sectionId}`;

        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            this.replaceBlock(html, `price-${sectionId}`);
            this.replaceBlock(html, `Inventory-${sectionId}`);
            this.replaceBlock(html, `Sku-${sectionId}`);
            this.replaceBlock(html, `Volume-Note-${sectionId}`);
            this.replaceBlock(html, `Quantity-Form-${sectionId}`);

            const newVariantInput = html.querySelector('input.product-variant-id, form[action*="/cart/add"] input[name="id"]');
            if (newVariantInput) {
              this.updateVariantId(newVariantInput.value);
            }

            const newSubmitButton = html.querySelector(`#ProductSubmitButton-${sectionId}`);
            const currentSubmitButton = this.querySelector(`#ProductSubmitButton-${sectionId}`);
            if (newSubmitButton && currentSubmitButton) {
              currentSubmitButton.innerHTML = newSubmitButton.innerHTML;

              if (newSubmitButton.hasAttribute('disabled')) {
                currentSubmitButton.setAttribute('disabled', 'disabled');
              } else {
                currentSubmitButton.removeAttribute('disabled');
              }
            }

            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: sectionId,
                html: html
              }
            });
          })
          .catch((error) => {
            console.error('Variant render error:', error);
          });
      }

      replaceBlock(html, id) {
        const current = this.querySelector(`#${id}`) || document.getElementById(id);
        const updated = html.getElementById(id);

        if (current && updated) {
          current.innerHTML = updated.innerHTML;
        }
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity, 10) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min, 10) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max, 10) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step, 10) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;

        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        this.quantityInput.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        if (!this.currentVariantInput || !this.currentVariantInput.value) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(`${this.dataset.url}?variant=${this.currentVariantInput.value}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.setQuantityBoundries();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (spinner) spinner.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityForm) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];

        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);

          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];

            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }

        this.quantityInput = this.querySelector('.quantity__input');
        this.quantityForm = this.querySelector('.product-form__quantity');
        this.currentVariantInput = this.querySelector('.product-variant-id');
      }
    }
  );
}